import * as L from 'leaflet';
import fetchJsonp from 'fetch-jsonp';
import debounce from 'lodash/debounce';
import uniqueId from 'lodash/uniqueId';
/**
 * The zipfinder control's default options.
 */
const CONTROL_ZIPFINDER_OPTIONS_DEFAULT = {
    position: 'topleft',
    labelText: 'Ort suchen',
    backendUri: 'https://fabrikapi.meteotest.ch/swisstools/zipinfo/{zip}/jsonp',
    outputUri: 'https://mos.meteotest.ch/output/point/{lat}/{lng}',
};
/**
 * The zipfinder control.
 */
export class ControlZipfinder extends L.Control {
    /**
     * This control adds a small search box to the widget.
     * With it one can search for localities by either name or ZIP code.
     *
     * The first result for the search query will be displayed on the map
     * as a marker with a popup that contains a link to the MOS meteogram
     * for that location.
     *
     * @param options - The zipfinder control options.
     * @param widget - The M.A.A.P. instance.
     */
    constructor(options, widget) {
        super(Object.assign(Object.assign({}, CONTROL_ZIPFINDER_OPTIONS_DEFAULT), options));
        this.widget = widget;
    }
    /**
     * The hook to execute when the control is added to the map.
     * @returns The container element to be added to the map.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onAdd() {
        const container = L.DomUtil.create('div', 'widget-control-zipfinder');
        L.DomEvent.disableClickPropagation(container);
        const id = uniqueId('zipfinder');
        const label = L.DomUtil.create('label');
        label.innerText = this.options.labelText;
        label.htmlFor = id;
        this.input = L.DomUtil.create('input');
        this.input.id = id;
        L.DomEvent.on(this.input, 'keyup', debounce(this.handleKeyboard, 300), this);
        this.clear = L.DomUtil.create('button');
        this.clear.type = 'button';
        this.clear.innerText = 'x';
        L.DomEvent.on(this.clear, 'click', this.handleClear, this);
        container.appendChild(label);
        container.appendChild(this.input);
        container.appendChild(this.clear);
        this.marker = L.marker([0, 0]);
        return container;
    }
    /**
     * The hook to execute when the control is removed from the map.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onRemove() {
        if (this.input) {
            L.DomEvent.off(this.input, 'keyup', debounce(this.handleKeyboard, 300), this);
        }
        if (this.clear) {
            L.DomEvent.on(this.clear, 'click', this.handleClear, this);
        }
    }
    /**
     * Clear the zipfinder input.
     */
    handleClear() {
        if (this.input) {
            this.input.value = '';
            this.input.dispatchEvent(new KeyboardEvent('keyup'));
        }
    }
    /**
     * Handle keyboard events executed on the input element.
     * @param event - The keyboard event.
     */
    handleKeyboard(event) {
        const target = event.target;
        if (target) {
            const zip = target.value.trim();
            if (zip.length > 0) {
                this.fetch(zip);
            }
            else {
                if (this.marker) {
                    this.marker.remove();
                }
            }
        }
    }
    /**
     * Fetch data from the API using the ZIP code.
     * @param zip - The postal code that should be sent to the API.
     */
    fetch(zip) {
        const uri = L.Util.template(this.options.backendUri, { zip });
        fetchJsonp(uri)
            .then((response) => {
            if (response.ok) {
                return response.json();
            }
            else {
                throw new Error(response.statusText || response.status);
            }
        })
            .then(this.handleResponse.bind(this))
            .catch(console.error.bind(console));
    }
    /**
     * Handle the API response.
     * @param data - The response from the API as JSON.
     */
    handleResponse(data) {
        const { zip, name, lat, lon: lng } = data;
        const uri = L.Util.template(this.options.outputUri, { lat, lng });
        const latlng = [lat, lng];
        if (this._map) {
            this._map.panTo(latlng);
            if (this.marker) {
                if (this.marker._map) {
                    this.marker.addTo(this._map);
                }
                this.marker.setLatLng(latlng);
                this.marker.bindPopup(`<a href="${uri}" target="_blank">${zip} ${name}</a>`);
                this.marker.openPopup();
            }
        }
    }
}
//# sourceMappingURL=ControlZipfinder.js.map